// Sales Activity report — touchpoints by type + what got created, in the
// range. Click a box to list every call, email, project, order… behind it
// (Stephanie 2026-10-16).
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { ReportShell, useReportRange } from "@/components/ReportShell";
import { ReportRowsTable, ReportTile, type ReportList } from "@/components/ReportRowsTable";

interface Report {
  range: { from: string; to: string };
  touchpointsByType: Record<string, number>;
  touchpointsTotal: number;
  creations: {
    projects: number;
    presentations: number;
    orders: number;
    invoices: number;
  };
  fetchedAt: string;
}

const TOUCHPOINT_TYPES: Array<{ key: string; label: string }> = [
  { key: "call", label: "Calls" },
  { key: "meeting", label: "Meetings" },
  { key: "email", label: "Emails" },
  { key: "note", label: "Notes" },
  { key: "sms", label: "SMS" },
];

const CREATIONS: Array<{ list: ReportList; key: keyof Report["creations"]; label: string; title: string }> = [
  { list: "projects", key: "projects", label: "Projects", title: "Projects created" },
  { list: "presentations", key: "presentations", label: "Presentations", title: "Presentations created" },
  { list: "orders", key: "orders", label: "Orders", title: "Orders created (estimates and sales orders)" },
  { list: "invoices", key: "invoices", label: "Invoices", title: "Invoices created" },
];

/** What the list under the boxes shows: all touchpoints, one type, or a creation list. */
type Pick = { list: "touchpoints"; type?: string } | { list: ReportList; type?: undefined };

export function ReportSalesActivityPage() {
  const { preset, setPreset, range, setCustom, repId, setRepId } = useReportRange();
  const [pick, setPick] = useState<Pick>({ list: "touchpoints" });
  const rep = repId ? `&rep_id=${repId}` : "";

  const { data, isLoading, isError } = useQuery({
    queryKey: ["reports", "sales-activity", range.from, range.to, repId],
    queryFn: () => api<Report>(`/reports/sales-activity?from=${range.from}&to=${range.to}${rep}`),
  });
  const typeLabel = TOUCHPOINT_TYPES.find((t) => t.key === pick.type)?.label;
  const title =
    pick.list === "touchpoints"
      ? typeLabel ?? "All touchpoints"
      : CREATIONS.find((c) => c.list === pick.list)?.title ?? "";

  return (
    <ReportShell
      title="Sales Activity"
      subtitle="Calls, meetings, emails and notes, and what got created. Click a box to see the list."
      preset={preset}
      onPresetChange={setPreset}
      range={range}
      onCustomRange={setCustom}
      repId={repId}
      onRepChange={setRepId}
      fetchedAt={data?.fetchedAt}
    >
      {isLoading ? (
        <div className="rounded-lg border border-brown-100 bg-white p-10 text-center text-sm text-brown-300">Loading…</div>
      ) : isError || !data ? (
        <div className="rounded-lg border border-rust-300 bg-rust-50 p-10 text-center text-sm text-rust-600">Couldn't load report.</div>
      ) : (
        <div className="space-y-8">
          <section>
            <h2 className="mb-3 text-[11px] font-medium uppercase tracking-[0.12em] text-muted">
              Touchpoints ·{" "}
              <button type="button" onClick={() => setPick({ list: "touchpoints" })} className="underline-offset-2 hover:underline">
                {data.touchpointsTotal.toLocaleString()} total
              </button>
            </h2>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
              {TOUCHPOINT_TYPES.map((t) => (
                <ReportTile
                  key={t.key}
                  label={t.label}
                  value={data.touchpointsByType[t.key] ?? 0}
                  sub=""
                  selected={pick.list === "touchpoints" && pick.type === t.key}
                  onSelect={() => setPick({ list: "touchpoints", type: t.key })}
                />
              ))}
            </div>
          </section>
          <section>
            <h2 className="mb-3 text-[11px] font-medium uppercase tracking-[0.12em] text-muted">Created in the range</h2>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              {CREATIONS.map((c) => (
                <ReportTile
                  key={c.list}
                  label={c.label}
                  value={data.creations[c.key]}
                  sub=""
                  selected={pick.list === c.list}
                  onSelect={() => setPick({ list: c.list })}
                />
              ))}
            </div>
          </section>
        </div>
      )}
      <ReportRowsTable list={pick.list} range={range} repId={repId} title={title} onlyStatus={pick.type} />
    </ReportShell>
  );
}
